import { useState, useCallback, useEffect } from 'react'
import { supabase } from '@/integrations/supabase/client'

interface CachedPermission {
  hasPermission: boolean
  timestamp: number
  companyId: string
  userId: string
}

const CACHE_DURATION = 15 * 60 * 1000 // 15 minutes
const PERMISSION_CACHE = new Map<string, CachedPermission>()

export function usePermissionCache() {
  const [isChecking, setIsChecking] = useState(false)

  const getCacheKey = (userId: string, companyId: string) => `${userId}-${companyId}`

  const getCachedPermission = useCallback((userId: string, companyId: string): boolean | null => {
    const key = getCacheKey(userId, companyId)
    const cached = PERMISSION_CACHE.get(key)
    
    if (!cached) return null
    
    const isExpired = Date.now() - cached.timestamp > CACHE_DURATION
    if (isExpired) {
      PERMISSION_CACHE.delete(key)
      return null
    }
    
    return cached.hasPermission
  }, [])

  const setCachedPermission = useCallback((userId: string, companyId: string, hasPermission: boolean) => {
    const key = getCacheKey(userId, companyId)
    PERMISSION_CACHE.set(key, {
      hasPermission,
      timestamp: Date.now(),
      companyId,
      userId
    })
  }, [])

  const checkCompanyPermission = useCallback(async (
    userId: string, 
    companyId: string,
    userRole?: string
  ): Promise<boolean> => {
    // Skip check for super_admin
    if (userRole === 'super_admin') {
      setCachedPermission(userId, companyId, true)
      return true
    }

    // Check cache first
    const cached = getCachedPermission(userId, companyId)
    if (cached !== null) {
      return cached
    }

    // Skip verification for temporary IDs
    if (typeof companyId === 'string' && companyId.startsWith('temp-')) {
      setCachedPermission(userId, companyId, true)
      return true
    }

    setIsChecking(true)
    try {
      const { data, error } = await supabase.rpc('user_has_company_permission', {
        user_id: userId,
        company_id: companyId,
        required_role: 'admin'
      })
      
      if (error) throw error
      
      const hasPermission = !!data
      setCachedPermission(userId, companyId, hasPermission)
      return hasPermission
    } catch (error) {
      console.error('Erro ao verificar permissão da empresa:', error)
      return false
    } finally {
      setIsChecking(false)
    }
  }, [getCachedPermission, setCachedPermission])

  const clearCache = useCallback(() => {
    PERMISSION_CACHE.clear()
  }, [])

  const clearAllPermissionCache = useCallback(() => {
    PERMISSION_CACHE.clear()
  }, [])

  // Clear cache on unmount
  useEffect(() => {
    return () => {
      // Don't clear cache on unmount - keep it for better performance
    }
  }, [])

  return {
    checkCompanyPermission,
    getCachedPermission,
    clearCache,
    clearAllPermissionCache,
    isChecking
  }
}
